import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MDBCard, MDBCardBody, MDBIcon, MDBSpinner } from 'mdb-react-ui-kit';
import { metaOAuthComplete } from '../../services/apiServices';

export default function CallbackSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [accountData, setAccountData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const gymid = state;

        console.log('🔐 Callback received on frontend:', {
            code: code ? `${code.substring(0, 20)}...` : null,
            gymid,
            url: window.location.href
        });

        if (!code || !gymid) {
            setError('Missing required parameters');
            setLoading(false);
            return;
        }

        completeOAuthFlow(code, gymid);
    }, [searchParams]);

    const completeOAuthFlow = async (code, gymid) => {
        try {
            const response = await metaOAuthComplete({ code, gymid })

            const data = await response.json();

            if (response.ok && data.success) {
                console.log('✅ WhatsApp connected successfully!');
                setAccountData(data.account);

                // Close this window/tab after 3 seconds
                setTimeout(() => {
                    window.close();
                }, 3000);
            } else {
                setError(data.message || 'Connection failed');
            }
        } catch (err) {
            console.error('❌ Error:', err);
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const goToDashboard = () => {
        navigate('/dashboard');
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                <MDBCard style={{ maxWidth: '450px', width: '100%', borderRadius: '16px' }}>
                    <MDBCardBody className="text-center p-5">
                        <MDBSpinner color="success" style={{ width: '4rem', height: '4rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                        <h3 className="mt-4 mb-2 fw-bold">Connecting WhatsApp...</h3>
                        <p className="text-muted">Please wait while we set up your business account</p>
                    </MDBCardBody>
                </MDBCard>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f85050 0%, #cb2e2e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                <MDBCard style={{ maxWidth: '450px', width: '100%', borderRadius: '16px' }}>
                    <MDBCardBody className="text-center p-5">
                        <div style={{
                            width: '80px',
                            height: '80px',
                            background: '#fff1f1',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px'
                        }}>
                            <MDBIcon fas icon="exclamation-triangle" size="3x" style={{ color: '#f85050' }} />
                        </div>
                        <h3 className="fw-bold mb-3" style={{ color: '#333' }}>
                            Connection Failed
                        </h3>
                        <div style={{
                            background: '#fff1f1',
                            padding: '16px',
                            borderRadius: '12px',
                            marginBottom: '24px',
                            color: '#cb2e2e',
                            border: '1px solid #ffcdcd'
                        }}>
                            {error}
                        </div>
                        <button
                            onClick={goToDashboard}
                            style={{
                                background: '#f85050',
                                color: 'white',
                                border: 'none',
                                padding: '14px 36px',
                                borderRadius: '50px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                transition: 'all 0.3s',
                                boxShadow: '0 4px 12px rgba(248, 80, 80, 0.3)'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#cb2e2e'}
                            onMouseOut={(e) => e.target.style.background = '#f85050'}
                        >
                            <MDBIcon fas icon="arrow-left" className="me-2" />
                            Back to Dashboard
                        </button>
                    </MDBCardBody>
                </MDBCard>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <MDBCard style={{ maxWidth: '450px', width: '100%', borderRadius: '16px' }}>
                <MDBCardBody className="text-center p-5">
                    <div style={{
                        width: '80px',
                        height: '80px',
                        background: '#e8f5e9',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                    }}>
                        <MDBIcon fas icon="check-circle" size="4x" style={{ color: '#25D366' }} />
                    </div>
                    <h3 className="fw-bold mb-3" style={{ color: '#25D366' }}>
                        Connected Successfully!
                    </h3>
                    <p className="text-muted mb-4">
                        Your WhatsApp Business account has been linked to your dashboard
                    </p>

                    <div style={{
                        background: '#f8f9fa',
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '24px'
                    }}>
                        <div className="d-flex align-items-center justify-content-center mb-3">
                            <MDBIcon fab icon="whatsapp" size="2x" style={{ color: '#25D366' }} />
                        </div>
                        <div style={{
                            background: 'white',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                        }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted">Phone Number:</span>
                                <span className="fw-bold">
                                    {accountData?.displayphonenumber || 'N/A'}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Business Name:</span>
                                <span className="fw-bold">
                                    {accountData?.verifiedname || 'WhatsApp Business'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={goToDashboard}
                        style={{
                            background: '#25D366',
                            color: 'white',
                            border: 'none',
                            padding: '14px 36px',
                            borderRadius: '50px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'all 0.3s',
                            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                            width: '100%'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#128C7E'}
                        onMouseOut={(e) => e.target.style.background = '#25D366'}
                    >
                        <MDBIcon fas icon="check" className="me-2" />
                        Continue to Dashboard
                    </button>

                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '16px' }}>
                        <MDBIcon fas icon="clock" className="me-1" />
                        This window will close automatically in 3 seconds
                    </p>
                </MDBCardBody>
            </MDBCard>
        </div>
    );
}